import {Alert, ChevronDown, Radio} from "@komplett/design-system";
import classNames from "classnames";
import React, {useState} from "react";

import {ConfigServiceGroupI} from "../../App.types";
import {setConfigService} from "../../slices/actions";
import {
    configServiceAdded,
    prepareConfigServiceItem,
} from "../../tracking/googleAnalytics";
import {useDispatch, useSelector} from "../../utils/hooks";
import strings from "../../utils/strings";
import icons from "../../utils/svgProvider";
import BuyButton from "../ui/button/BuyButton";
import SuccessButton from "../ui/button/SuccessButton";

import {
    getCartParams,
    hasAccessories as groupHasAccessories,
    toOptions,
} from "./ConfigServices.utils";

interface ConfigServiceCardPropsI {
    group: ConfigServiceGroupI;
}

export const ConfigServiceCard = ({group}: ConfigServiceCardPropsI) => {
    const dispatch = useDispatch();
    const cartItemId = useSelector(
        (state) => state.popup.products.mainProduct.cartItemId,
    );

    const options = toOptions(group);
    const hasAccessories = groupHasAccessories(group);
    const hasAlternatives = options.length > 1;
    const hasInformationText = !!group.informationText?.trim();
    const showDeliveryTime =
        hasAccessories &&
        group.configureBeforeDelivery &&
        !!strings.configServicesDeliveryTime?.trim();

    const [pickedOptionId, setPickedOptionId] = useState<string>(null);
    const [isExpanded, toggleExpanded] = useState(false);
    const [status, setStatus] = useState<"idle" | "loading" | "added" | "error">(
        "idle",
    );

    const isAdded = status === "added";
    // The read more text and the alternatives share one accordion
    const hasDetails = (hasAlternatives || hasInformationText) && !isAdded;
    const toggleLabel = !hasAlternatives
        ? strings.configServicesReadMore
        : hasInformationText
          ? isExpanded
            ? strings.configServicesHideOthersAndReadMore
            : strings.configServicesSeeOthersAndReadMore
          : isExpanded
            ? strings.configServicesHideOthers
            : strings.configServicesSeeOthers;

    const activeOption =
        options.find((option) => option.id === pickedOptionId) ?? options[0];
    const detailsId = `config-service-details-${group.serviceGroupId}`;

    const addToCart = () => {
        if (status === "loading") {
            return;
        }

        setStatus("loading");

        dispatch(
            setConfigService({
                cartItemId,
                materialId: group.configuredProductId,
                serviceGroupId: group.serviceGroupId,
                ...getCartParams(group, activeOption),
            }),
        )
            .unwrap()
            .then(() => {
                setStatus("added");
                configServiceAdded(prepareConfigServiceItem(activeOption));
            })
            .catch(() => setStatus("error"));
    };

    return (
        <div
            className={classNames("addtocart-configService", {
                "addtocart-configService--noImage": !activeOption.imageUrl,
            })}
            data-testid={`config-service-${group.serviceGroupId}`}
        >
            {activeOption.imageUrl && (
                <img
                    className="addtocart-configService__image"
                    src={activeOption.imageUrl}
                    alt={activeOption.name}
                    width="70"
                    height="70"
                    loading="lazy"
                    decoding="async"
                />
            )}

            <div className="addtocart-configService__info">
                <h3>{hasAccessories ? group.name : activeOption.name}</h3>
                {hasAccessories && (
                    <p
                        className="addtocart-configService__name"
                        data-testid="config-service-name"
                    >
                        {activeOption.name}
                    </p>
                )}
                {!group.children.length && group.description && (
                    <p
                        className="addtocart-configService__name"
                        data-testid="config-service-description"
                    >
                        {group.description}
                    </p>
                )}

                {showDeliveryTime && (
                    <p className="addtocart-configService__delivery">
                        {icons.truck}
                        {strings.configServicesDeliveryTime}
                    </p>
                )}
            </div>

            <strong
                className="addtocart-configService__price"
                data-testid="config-service-price"
            >
                {activeOption.priceFormatted}
            </strong>

            {hasDetails && (
                <button
                    type="button"
                    className="read-more-link"
                    aria-expanded={isExpanded}
                    aria-controls={detailsId}
                    onClick={() => toggleExpanded(!isExpanded)}
                >
                    {toggleLabel}
                    <span
                        className={classNames("read-more-link-icon", {
                            "is-open": isExpanded,
                        })}
                    >
						<ChevronDown size={14}/>
					</span>
                </button>
            )}

            {hasDetails && isExpanded && (
                <div id={detailsId} className="addtocart-configService__details">
                    {hasInformationText && (
                        <div
                            className="addtocart-configService__information"
                            data-testid="config-service-information"
                            dangerouslySetInnerHTML={{__html: group.informationText}}
                        />
                    )}

                    {hasAlternatives && (
                        <ul className="addtocart-configService__options">
                            {options.map((option) => (
                                <li key={option.id}>
                                    <Radio
                                        variant="neutral"
                                        name={`config-service-${group.serviceGroupId}`}
                                        value={option.id}
                                        checked={option.id === activeOption.id}
                                        onChange={() => setPickedOptionId(option.id)}
                                        className="addtocart-configService__option"
                                        labelProps={{className: "addtocart-configService__option-label"}}
                                        label={
                                            <>
												<span className="addtocart-configService__option-text">
													<span className="addtocart-configService__option-name">
														{option.name}
													</span>
                                                    {option.description && (
                                                        <span className="addtocart-configService__option-description">
															{option.description}
														</span>
                                                    )}
												</span>
                                                <span className="addtocart-configService__option-price">
													{option.priceFormatted}
												</span>
                                            </>
                                        }
                                    />
                                </li>
                            ))}
                        </ul>
                    )}

                    {hasAlternatives && showDeliveryTime && (
                        <p className="addtocart-configService__note">
                            {strings.configServicesInstallationIncluded}
                        </p>
                    )}
                </div>
            )}

            <span aria-live="polite" className="addtocart-buttonContainer">
				{isAdded ? (
                    <SuccessButton
                        bought={strings.bought}
                        label={strings.bought}
                        onClickHandler={() => {
                        }}
                    />
                ) : (
                    <BuyButton
                        variant="dark"
                        loading={status === "loading"}
                        label={strings.buy}
                        onClickHandler={addToCart}
                    />
                )}
			</span>

            {status === "error" && (
                <Alert variant="error" className="addtocart-configService__error">
                    {strings.configServicesError}
                </Alert>
            )}
        </div>
    );
};
